import { useState } from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/shadcn/button'
import { Separator } from '@/components/shadcn/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs'
import { PageLayout } from '@/components/layout/PageLayout'
import { notify } from '@/lib/notify'
import {
  useGetApiCompaniesCurrent,
  useGetApiCompaniesCurrentSubcompanies,
  useDeleteApiCompaniesCurrentSubcompaniesSubCompanyId,
  usePatchApiCompaniesCurrentInvoiceemails,
  usePatchApiCompaniesCurrentSubcompaniesSubCompanyIdInvoiceemails,
  getGetApiCompaniesCurrentSubcompaniesQueryKey,
  getGetApiCompaniesCurrentQueryKey,
} from '@/api/generated/company-management-user/company-management-user'
import type { SubCompanyModel } from '@/api/model/subCompanyModel'
import { PermissionEntityType } from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'
import { CompanyInfoCard } from './components/CompanyInfoCard'
import { SubCompanyDialog } from './components/SubCompanyDialog'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'

// Tab value of the main company. Subcompanies use their id as the tab value.
const MAIN_TAB = 'main'

export default function CompanyPage() {
  const { t } = useTranslation(['company', 'common'])
  const { data: companyData, isLoading: companyLoading, isError: companyError } = useGetApiCompaniesCurrent()
  const { data: subData, isLoading: subLoading, isError: subError } = useGetApiCompaniesCurrentSubcompanies()

  const { userHasPermission, userHasEntityPermission } = usePermissions()
  const P = PermissionEntityType
  const canCreateSub = userHasPermission(P.SubCompanies, 'Create')

  const company = companyData?.data
  // Only show sub-companies the user may view (general SubCompanies.View or
  // a per-entity grant for that sub).
  const subcompanies: SubCompanyModel[] = (subData?.data ?? []).filter((sub) =>
    userHasEntityPermission(P.SubCompanies, 'View', sub.id),
  )

  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = useState(MAIN_TAB)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedSub, setSelectedSub] = useState<SubCompanyModel | null>(null)
  const [confirmSub, setConfirmSub] = useState<SubCompanyModel | null>(null)

  const handleCreate = () => {
    setSelectedSub(null)
    setDialogOpen(true)
  }

  const handleEdit = (sub: SubCompanyModel) => {
    setSelectedSub(sub)
    setDialogOpen(true)
  }

  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open)
    if (!open) setSelectedSub(null)
  }

  const { mutate: deleteSub, isPending: isDeleting } = useDeleteApiCompaniesCurrentSubcompaniesSubCompanyId({
    mutation: {
      onSuccess: () => {
        notify.success(t('toasts.subCompanyDeleted'))
        // The active tab could be the section just deleted → back to the main company.
        setActiveTab(MAIN_TAB)
        setConfirmSub(null)
        queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentSubcompaniesQueryKey() })
      },
      onError: (error) => notify.apiError(error, t('toasts.deleteError')),
    },
  })

  const { mutateAsync: saveCompanyEmails, isPending: isSavingCompanyEmails } =
    usePatchApiCompaniesCurrentInvoiceemails({
      mutation: {
        onSuccess: () => {
          notify.success(t('toasts.emailsSaved'))
          queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentQueryKey() })
        },
        onError: (error) => notify.apiError(error, t('toasts.saveError')),
      },
    })

  const { mutateAsync: saveSubEmails, isPending: isSavingSubEmails } =
    usePatchApiCompaniesCurrentSubcompaniesSubCompanyIdInvoiceemails({
      mutation: {
        onSuccess: () => {
          notify.success(t('toasts.emailsSaved'))
          queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentSubcompaniesQueryKey() })
        },
        onError: (error) => notify.apiError(error, t('toasts.saveError')),
      },
    })

  const handleDelete = (sub: SubCompanyModel) => setConfirmSub(sub)

  return (
    <PageLayout>
      <PageHeader title={t('header.title')} description={t('header.description')}>
        <PageHeaderActions>
          <Button className="h-9 gap-2" onClick={handleCreate} disabled={!canCreateSub}>
            <Plus className="size-4" />
            <span className="hidden sm:inline">{t('header.createSubCompany')}</span>
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <LoaderGeneral isLoading={companyLoading || subLoading} isError={companyError || subError}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            {company && <TabsTrigger value={MAIN_TAB}>{company.name ?? t('tabs.companyFallback')}</TabsTrigger>}
            {subcompanies.map((sub, i) => (
              <TabsTrigger key={sub.id} value={String(sub.id)}>
                {sub.name ?? t('tabs.subCompanyFallback', { index: i + 1 })}
              </TabsTrigger>
            ))}
          </TabsList>

          <Separator className="mt-2 mb-2" />

          {/* Main company — emails inline editable */}
          {company && (
            <TabsContent value={MAIN_TAB} className="flex flex-wrap gap-6">
              <CompanyInfoCard
                title={t('infoCard.billingAddressTitle')}
                name={company.name}
                phoneNumber={company.phoneNumber}
                taxId={company.taxId}
                invoiceEmails={company.invoiceEmails}
                address={company.address}
                showSepaInstruction
                onSaveEmails={(emails) => saveCompanyEmails({ data: { invoiceEmails: emails } })}
                isSavingEmails={isSavingCompanyEmails}
              />
            </TabsContent>
          )}

          {/* Subcompanies — emails inline editable, rest via dialog */}
          {subcompanies.map((sub) => (
            <TabsContent key={sub.id} value={String(sub.id)} className="flex flex-wrap gap-6">
              <CompanyInfoCard
                title={t('infoCard.billingAddressTitle')}
                name={sub.name}
                phoneNumber={sub.phoneNumber}
                taxId={sub.taxId}
                invoiceEmails={sub.invoiceEmails}
                address={sub.address}
                canEdit={userHasEntityPermission(P.SubCompanies, 'Edit', sub.id)}
                onEdit={() => handleEdit(sub)}
                canDelete={userHasEntityPermission(P.SubCompanies, 'Delete', sub.id)}
                onDelete={() => handleDelete(sub)}
                onSaveEmails={
                  userHasEntityPermission(P.SubCompanies, 'Edit', sub.id)
                    ? (emails) => saveSubEmails({ subCompanyId: sub.id, data: { invoiceEmails: emails } })
                    : undefined
                }
                isSavingEmails={isSavingSubEmails}
              />
            </TabsContent>
          ))}
        </Tabs>
      </LoaderGeneral>

      <SubCompanyDialog
        open={dialogOpen}
        onClose={() => handleDialogOpenChange(false)}
        subCompany={selectedSub}
      />

      <ConfirmDialog
        open={!!confirmSub}
        title={t('confirmDelete.title')}
        description={t('confirmDelete.description', { name: confirmSub?.name ?? '' })}
        confirmLabel={t('buttons.delete', { ns: 'common' })}
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
        onConfirm={() => confirmSub && deleteSub({ subCompanyId: confirmSub.id })}
        onClose={() => setConfirmSub(null)}
      />
    </PageLayout>
  )
}
